import React, { useState } from 'react';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Collapse,
  Divider
} from '@mui/material';
import {
  Home as HomeIcon,
  Storage as DataIcon,
  Code as CodeIcon,
  BarChart as ChartBarIcon,
  CheckCircle as CheckCircleIcon,
  ContentCut as CutIcon,
  Person as UserCheckIcon,
  Timeline as ChartLineIcon,
  Eco as LeafIcon,
  AutoAwesome as OptinMonsterIcon,
  PieChart as ChartPieIcon,
  Warning as ExclamationTriangleIcon,
  Analytics as ChartBoxIcon,
  AccountTree as TreeIcon,
  Error as ExclamationCircleIcon,
  Shuffle as RandomIcon,
  QueryStats as ChartSimpleIcon,
  AccessTime as ClockIcon,
  Payments as MoneyBillWaveIcon,
  Calculate as CalculatorIcon,
  AddCircle as PlusCircleIcon,
  Close as TimesCircleIcon,
  Security as ShieldAltIcon,
  ExpandLess,
  ExpandMore,
  Info as InfoCircleIcon,
  Email as EnvelopeIcon,
  ArrowDownward as ArrowDownIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';

const drawerWidth = 280;

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // États pour tous les sous-menus
  const [openDataAnalysis, setOpenDataAnalysis] = useState(false);
  const [openDataQuality, setOpenDataQuality] = useState(false);
  const [openDiscretization, setOpenDiscretization] = useState(false);
  const [openExtremes, setOpenExtremes] = useState(false);
  const [openRiskDiff, setOpenRiskDiff] = useState(false);
  const [openMRP, setOpenMRP] = useState(false);
  const [openCFProjection, setOpenCFProjection] = useState(false);
  const [openRiskQuant, setOpenRiskQuant] = useState(false);
  const [openMoCDownturn, setOpenMoCDownturn] = useState(false);

  const isActive = (path) => location.pathname === path;

  const menuItemStyle = {
    borderRadius: 2,
    mx: 1,
    mb: 0.5,
    transition: 'all 0.3s ease',
    borderLeft: '3px solid transparent',
    '&.Mui-selected': {
      bgcolor: '#1abc9c',
      borderLeftColor: '#fff',
      '&:hover': { bgcolor: '#16a085' },
    },
    '&:hover': {
      bgcolor: '#34495e',
      borderLeftColor: '#1abc9c',
    },
  };

  const submenuItemStyle = {
    pl: 5,
    ...menuItemStyle,
  };

  const nestedSubmenuItemStyle = {
    pl: 7,
    ...menuItemStyle,
  };

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          background: '#2c3e50',
          color: 'white',
          borderRight: 'none',
          boxShadow: '2px 0 5px rgba(0,0,0,0.1)',
        },
      }}
    >
      {/* Header */}
      <Box sx={{ bgcolor: '#1abc9c', py: 2.5, textAlign: 'center' }}>
        <Typography variant="h6" sx={{ fontWeight: 600, letterSpacing: 1 }}>
          <ChartLineIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          LGD & CCF
        </Typography>
      </Box>

      <List sx={{ p: 1 }}>
        {/* Home */}
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => navigate('/')}
            selected={isActive('/')}
            sx={menuItemStyle}
          >
            <ListItemIcon sx={{ color: 'white', minWidth: 35 }}>
              <HomeIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Home" />
          </ListItemButton>
        </ListItem>

        {/* Data */}
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => navigate('/data')}
            selected={isActive('/data')}
            sx={menuItemStyle}
          >
            <ListItemIcon sx={{ color: 'white', minWidth: 35 }}>
              <DataIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Import & Data" />
          </ListItemButton>
        </ListItem>

        {/* Python Playground */}
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => navigate('/python-playground')}
            selected={isActive('/python-playground')}
            sx={{
              ...menuItemStyle,
              bgcolor: isActive('/python-playground') ? '#1abc9c' : 'transparent',
            }}
          >
            <ListItemIcon sx={{ color: 'white', minWidth: 35 }}>
              <CodeIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Python Playground" />
          </ListItemButton>
        </ListItem>

        {/* Data Analysis */}
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => setOpenDataAnalysis(!openDataAnalysis)}
            sx={menuItemStyle}
          >
            <ListItemIcon sx={{ color: 'white', minWidth: 35 }}>
              <ChartBarIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Data Analysis" />
            {openDataAnalysis ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>

        <Collapse in={openDataAnalysis} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {/* Data Quality */}
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => setOpenDataQuality(!openDataQuality)}
                sx={submenuItemStyle}
              >
                <ListItemIcon sx={{ color: 'white', minWidth: 35 }}>
                  <CheckCircleIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Data Quality" />
                {openDataQuality ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
            </ListItem>
            <Collapse in={openDataQuality} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <ListItem disablePadding>
                  <ListItemButton onClick={() => navigate('/missing-rate')} sx={nestedSubmenuItemStyle}>
                    <ListItemText primary="Missing rate" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton onClick={() => navigate('/missing-imputation')} sx={nestedSubmenuItemStyle}>
                    <ListItemText primary="Missing imputation" />
                  </ListItemButton>
                </ListItem>
              </List>
            </Collapse>

            {/* Discretization */}
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => setOpenDiscretization(!openDiscretization)}
                sx={submenuItemStyle}
              >
                <ListItemIcon sx={{ color: 'white', minWidth: 35 }}>
                  <CutIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Discretization" />
                {openDiscretization ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
            </ListItem>
            <Collapse in={openDiscretization} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <ListItem disablePadding>
                  <ListItemButton onClick={() => navigate('/expert-cut')} sx={nestedSubmenuItemStyle}>
                    <ListItemText primary="Expert cut" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton onClick={() => navigate('/isotonic-regression')} sx={nestedSubmenuItemStyle}>
                    <ListItemText primary="Isotonic regression" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton onClick={() => navigate('/monotonic-lightgbm')} sx={nestedSubmenuItemStyle}>
                    <ListItemText primary="Monotonic Light GBM" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton onClick={() => navigate('/monotonic-binning')} sx={nestedSubmenuItemStyle}>
                    <ListItemText primary="Monotonic Optimal Binning" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton onClick={() => navigate('/quantile-cut')} sx={nestedSubmenuItemStyle}>
                    <ListItemText primary="Quantile cut" />
                  </ListItemButton>
                </ListItem>
              </List>
            </Collapse>

            {/* Extremes values */}
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => setOpenExtremes(!openExtremes)}
                sx={submenuItemStyle}
              >
                <ListItemIcon sx={{ color: 'white', minWidth: 35 }}>
                  <ExclamationTriangleIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Extremes values" />
                {openExtremes ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
            </ListItem>
            <Collapse in={openExtremes} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <ListItem disablePadding>
                  <ListItemButton onClick={() => navigate('/iqr')} sx={nestedSubmenuItemStyle}>
                    <ListItemText primary="IQR" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton onClick={() => navigate('/isolation-forest')} sx={nestedSubmenuItemStyle}>
                    <ListItemText primary="Isolation Forest" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton onClick={() => navigate('/zscore')} sx={nestedSubmenuItemStyle}>
                    <ListItemText primary="ZScore" />
                  </ListItemButton>
                </ListItem>
              </List>
            </Collapse>
          </List>
        </Collapse>

        {/* Risk Diff */}
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => setOpenRiskDiff(!openRiskDiff)}
            sx={menuItemStyle}
          >
            <ListItemIcon sx={{ color: 'white', minWidth: 35 }}>
              <ExclamationCircleIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Risk Diff" />
            {openRiskDiff ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>
        <Collapse in={openRiskDiff} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItem disablePadding>
              <ListItemButton onClick={() => navigate('/sampling')} sx={submenuItemStyle}>
                <ListItemText primary="Sampling" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton onClick={() => navigate('/cart')} sx={submenuItemStyle}>
                <ListItemText primary="CART" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton onClick={() => navigate('/performance-testing')} sx={submenuItemStyle}>
                <ListItemText primary="Performance & Testing" />
              </ListItemButton>
            </ListItem>
          </List>
        </Collapse>

        {/* Maximum Recovery Period */}
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => setOpenMRP(!openMRP)}
            sx={menuItemStyle}
          >
            <ListItemIcon sx={{ color: 'white', minWidth: 35 }}>
              <ClockIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Max Recovery Period" />
            {openMRP ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>
        <Collapse in={openMRP} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItem disablePadding>
              <ListItemButton onClick={() => navigate('/mrp-cashflows')} sx={submenuItemStyle}>
                <ListItemText primary="Cash flows" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton onClick={() => navigate('/mrp-numbers')} sx={submenuItemStyle}>
                <ListItemText primary="Number" />
              </ListItemButton>
            </ListItem>
          </List>
        </Collapse>

        {/* CF Projection */}
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => setOpenCFProjection(!openCFProjection)}
            sx={menuItemStyle}
          >
            <ListItemIcon sx={{ color: 'white', minWidth: 35 }}>
              <ChartLineIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="CF Projection" />
            {openCFProjection ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>
        <Collapse in={openCFProjection} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItem disablePadding>
              <ListItemButton onClick={() => navigate('/additive-projection')} sx={submenuItemStyle}>
                <ListItemText primary="Additive model" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton onClick={() => navigate('/multiplicative-projection')} sx={submenuItemStyle}>
                <ListItemText primary="Multiplicative model" />
              </ListItemButton>
            </ListItem>
          </List>
        </Collapse>

        {/* Risk quantification */}
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => setOpenRiskQuant(!openRiskQuant)}
            sx={menuItemStyle}
          >
            <ListItemIcon sx={{ color: 'white', minWidth: 35 }}>
              <ShieldAltIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Risk quantification" />
            {openRiskQuant ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>
        <Collapse in={openRiskQuant} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItem disablePadding>
              <ListItemButton onClick={() => navigate('/lra')} sx={submenuItemStyle}>
                <ListItemText primary="LRA" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton onClick={() => navigate('/moc-c')} sx={submenuItemStyle}>
                <ListItemText primary="MoC C" />
              </ListItemButton>
            </ListItem>

            {/* MoC downturn - Nested */}
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => setOpenMoCDownturn(!openMoCDownturn)}
                sx={submenuItemStyle}
              >
                <ListItemText primary="MoC downturn" />
                {openMoCDownturn ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
            </ListItem>
            <Collapse in={openMoCDownturn} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <ListItem disablePadding>
                  <ListItemButton onClick={() => navigate('/dwt-identification')} sx={nestedSubmenuItemStyle}>
                    <ListItemText primary="Dwt Identification" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton onClick={() => navigate('/dwt-quantification')} sx={nestedSubmenuItemStyle}>
                    <ListItemText primary="Dwt quantification" />
                  </ListItemButton>
                </ListItem>
              </List>
            </Collapse>
          </List>
        </Collapse>
      </List>

      {/* Footer */}
      <Box sx={{ mt: 'auto', pt: 1, borderTop: '1px solid #465668' }}>
        <List sx={{ p: 1 }}>
          <ListItem disablePadding>
            <ListItemButton onClick={() => navigate('/about')} sx={menuItemStyle}>
              <ListItemIcon sx={{ color: 'white', minWidth: 35 }}>
                <InfoCircleIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="About" />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton onClick={() => navigate('/contact')} sx={menuItemStyle}>
              <ListItemIcon sx={{ color: 'white', minWidth: 35 }}>
                <EnvelopeIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Contact" />
            </ListItemButton>
          </ListItem>
        </List>
      </Box>
    </Drawer>
  );
};

export default Sidebar;
